import { Component } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { findIndex, isEmpty } from 'underscore';
import { message } from 'antd';
import DOM from './driversView';
import * as DriverActions from '../../core/driver/driverActions';
import * as AssetActions from '../../core/asset/assetActions';
import * as DivisionActions from '../../core/division/divisionActions';
import * as reportActions from '../../core/report/reportActions';
import {
    getTodayEndInServerFormat,
    getTodayStartInServerFormat,
    isoStandardDateTimeFormat,
} from '../../core/utils/dateUtils';
import { fetchApiAuth } from '../../core/utils/api';
import { triggerErrorNotification } from '../../core/utils/notificationUtils';
import { saveAs } from 'file-saver';

const { DB_DATE_FORMAT } = require('../../core/constants').default;

class DriversView extends Component {
    constructor(props) {
        super(props);
        this.view = DOM;

        this.state = {
            modalAssignToAssets: false,
            modalEdit: false,
            selectedAsset: {},
            currentTabKey: '1',

            // ------------------ Journey states ------------------
            journeyHistoryTabLoaded: false,
            journeyHistoryTabFilter: {
                asset_info: '',
                division: '',
                registration_year: '',
                make: '',
                fuel_type: '',
                current_location: '',
                page: 1,
                date_from: getTodayStartInServerFormat(),
                date_to: getTodayEndInServerFormat(),
            },
            driverJourneys: [],
            driverJourneysPagination: {
                currentPage: 1,
                perPageCount: 10,
                totalRecords: 0,
            },
            isLoadingJourneys: false,
            isExportingJourneyHistoryCsv: false,

            // ------------------ Driver Events states ------------------
            driverEvents: [],
            driverEventsPagination: {
                currentPage: 1,
                perPageCount: 10,
                totalRecords: 0,
            },
            eventsHistoryTabLoaded: false,
            eventsHistoryTabFilter: {
                asset_info: '',
                division: '',
                registration_year: '',
                make: '',
                fuel_type: '',
                current_location: '',
                page: 1,
                date_from: getTodayStartInServerFormat(),
                date_to: getTodayEndInServerFormat(),
            },
            isExportingDriverEventHistoryCsv: false,
            isLoadingEvents: false,

            // ------------------ Service Alerts states ------------------
            serviceAlertsTabLoaded: false,
            serviceAlertsTabFilter: {
                asset_info: '',
                division: '',
                registration_year: '',
                make: '',
                fuel_type: '',
                current_location: '',
                page: 1,
            },

            // ------------------ Delete & modal states ------------------
            deleteDataConfirmButtonVisible: false,
            isDeleteDataModalVisible: false,
            deleteDataModalInput: null,
            displayFilter: true,
        };
    }

    componentDidMount() {
        const {
            driverId,
            actions,
            divisions,
            driverList,
        } = this.props;

        // fetch initial data
        actions.getSingleDriverRequest({ driverId });
        actions.tagLimitReachedRequest({ driver_id: driverId });
        actions.getAssetListRequest();

        if (divisions.length === 0) {
            actions.getDivisionOptionsRequest();
        }
        if (driverList.length <= 0) {
            actions.getDriverListRequest();
        }
        this.fetchJourneyData();
    }

    componentDidUpdate(prevProps) {
        const {
            driver,
            actions,
            driverId,
        } = this.props;
        const { assignAssetFetching } = driver;

        // Refresh driver data if asset assignment completed
        if (!assignAssetFetching && assignAssetFetching !== prevProps.driver.assignAssetFetching) {
            actions.getSingleDriverRequest({ driverId });
        }
    }

    componentWillUnmount() {
        const { actions } = this.props;
        actions.setDriverJourneyFilters({});
    }

    handleTabChange(tabIndex) {
        const { driverId, driverViewJourneyFilters } = this.props;

        if (tabIndex === '1') {
            this.setState({
                driverJourneys: [],
                isLoadingJourneys: true,
                journeyHistoryTabFilter: {
                    asset_info: '',
                    division: '',
                    registration_year: '',
                    make: '',
                    fuel_type: '',
                    current_location: '',
                    page: 1,
                    date_from: getTodayStartInServerFormat(),
                    date_to: getTodayEndInServerFormat(),
                },
                currentTabKey: tabIndex,
            }, () => {
                this.fetchJourneyData();
            });

            return;
        }

        if (tabIndex === '2') {
            this.setState({
                driverEvents: [],
                isLoadingEvents: true,
                eventsHistoryTabFilter: {
                    asset_info: '',
                    division: '',
                    registration_year: '',
                    make: '',
                    fuel_type: '',
                    current_location: '',
                    page: 1,
                    date_from: getTodayStartInServerFormat(),
                    date_to: getTodayEndInServerFormat(),
                },
                currentTabKey: tabIndex,
            }, () => {
                let params = {
                    driverId,
                    ...this.state.eventsHistoryTabFilter,
                };
                if (driverViewJourneyFilters?.length) {
                    const matches = driverViewJourneyFilters.filter(f => parseInt(f.driverId) === parseInt(driverId));
                    if (matches?.length) {
                        params = matches[0].filters;
                    }
                }
                this.getDriverEvents(params);
            });

            return;
        }

        // For all other tabs
        this.setState({ currentTabKey: tabIndex });
    }

    // ------------------ Delete Modal Logic ------------------
    showModal = () => {
        this.setState({ isDeleteDataModalVisible: true });
    };

    saveDeleteText = (text) => {
        this.setState({ deleteDataModalInput: text });
        this.setState({
            deleteDataConfirmButtonVisible: text.toLowerCase() === 'delete',
        });
    };

    handleOk = () => {
        const { deleteDataModalInput } = this.state;
        const {
            actions,
            driverId,
        } = this.props;

        if (deleteDataModalInput && deleteDataModalInput.toLowerCase() === 'delete') {
            // make api request to delete data
            actions.deleteDriverDataRequest({ driverId });
        }
        this.setState({ isDeleteDataModalVisible: false });
    };

    handleCancel = () => {
        this.setState({
            isDeleteDataModalVisible: false,
            deleteDataModalInput: null,
        });
    };

    // ------------------ Tag & Assign Asset Logic ------------------
    requestTag = () => {
        const {
            driverId,
            actions,
        } = this.props;
        actions.requestTagRequest({ user_id: driverId });
    };

    toggleModalAssign = () => {
        this.setState((prevState) => ({
            modalAssignToAssets: !prevState.modalAssignToAssets,
        }));
    };

    toggleModalEdit = () => {
        this.setState((prevState) => ({
            modalEdit: !prevState.modalEdit,
        }));
    };

    selectAsset(id) {
        const { asset } = this.props;
        const singleAssetKey = findIndex(asset.assetList, (val) => val.id === id);
        if (singleAssetKey !== -1) {
            this.setState({
                selectedAsset: asset.assetList[singleAssetKey],
            });
        }
    }

    assignAsset() {
        const { selectedAsset } = this.state;
        const {
            driverId,
            actions,
        } = this.props;

        if (isEmpty(selectedAsset)) {
            message.error('Please select asset');
            return;
        }

        actions.assignAssetRequest({
            assetId: selectedAsset.id,
            driverId,
        });
        this.setState({
            modalAssignToAssets: false,
        });
    }

    // ------------------ Editing Driver ------------------
    updateDriver = (values) => {
        const {
            actions,
            match,
        } = this.props;
        const { driverId } = match.params;
        actions.updateDriverRequest({
            driverId,
            ...values,
        });
        this.setState({ modalEdit: true });
    };

    updateDriverPicture(data) {
        const {
            actions,
            driverId,
        } = this.props;
        actions.uploadImageRequest({
            image: data,
            driverId,
        });
    }

    async getDriverJourneys(params) {
        this.setState({ isLoadingJourneys: true });

        // Add a per page param to the request
        params.per_page = 10;

        try {
            const response = await fetchApiAuth({
                method: 'GET',
                url: '/report/get-timesheet-report',
                params,
            });

            const {
                data,
                headers,
            } = response;

            const total = parseInt(headers['x-pagination-total-count'] || '0', 10);
            const perPage = parseInt(headers['x-pagination-per-page'] || '10', 10);
            const current = parseInt(headers['x-pagination-current-page'] || '1', 10);

            this.setState({
                driverJourneys: data || [],
                driverJourneysPagination: {
                    currentPage: current,
                    perPageCount: perPage,
                    totalRecords: total,
                },
            });
        } catch (err) {
            triggerErrorNotification(err.response ? err.response.data.message : err.message);
        } finally {
            this.setState({ isLoadingJourneys: false });
        }
    }

    setJourneyHistoryTabLoaded = () => {
        this.setState({ journeyHistoryTabLoaded: true });
    };

    onJourneyHistoryTabPageChange = (page) => {
        const { match } = this.props;
        const { driverId } = match.params;
        const { journeyHistoryTabFilter } = this.state;

        // Update local filter's page
        this.setState(
            {
                journeyHistoryTabFilter: {
                    ...journeyHistoryTabFilter,
                    page,
                },
            },
            () => {
                // call the API
                this.getDriverJourneys({
                    driver: [driverId],
                    asset_or_driver: 'drivers',
                    journey_or_day: 'journeys',
                    ...this.convertJourneyFiltersForApi(this.state.journeyHistoryTabFilter),
                });
            },
        );
    };

    onJourneyHistoryTabFilterChange = (values) => {
        const { match, actions } = this.props;
        const { driverId } = match.params;

        // Force page=1
        const updatedFilters = {
            ...values,
            page: 1,
        };

        this.setState(
            { journeyHistoryTabFilter: updatedFilters },
            () => {
                const params = {
                    driver: [driverId],
                    asset_or_driver: 'drivers',
                    journey_or_day: 'journeys',
                    ...this.convertJourneyFiltersForApi(this.state.journeyHistoryTabFilter),
                };
                actions.setDriverViewJourneyFilters({
                    driverId,
                    filters: params,
                });
                this.getDriverJourneys(params);
            },
        );
    };

    onJourneyHistoryTabSortChange = (pagination, filters, sorter) => {
        const { match } = this.props;
        const { driverId } = match.params;
        const { journeyHistoryTabFilter } = this.state;

        // Example: Convert the local filters for the API again
        const params = {
            driver: [driverId],
            asset_or_driver: 'drivers',
            journey_or_day: 'journeys',
            ...this.convertJourneyFiltersForApi(journeyHistoryTabFilter),
            page: 1,
        };

        if (sorter.field) {
            params.field = sorter.field;
            params.order = sorter.order === 'ascend' ? 'asc' : 'desc';
        }
        this.getDriverJourneys(params);
    };

    convertJourneyFiltersForApi = (filters) => {
        const output = { ...filters };

        // Convert date_from/date_to if they are dayjs objects
        if (output.date_from && typeof output.date_from === 'object') {
            output.date_from = output.date_from.format(isoStandardDateTimeFormat);
        }
        if (output.date_to && typeof output.date_to === 'object') {
            output.date_to = output.date_to.format(isoStandardDateTimeFormat);
        }

        return output;
    };

    onExportJourneyHistoryCSV = async () => {
        this.setState({ isExportingJourneyHistoryCsv: true });
        try {
            const { match } = this.props;
            const { driverId } = match.params;
            const { journeyHistoryTabFilter } = this.state;

            // Build your query
            const params = {
                driver: [driverId],
                asset_or_driver: 'drivers',
                journey_or_day: 'journeys',
                ...this.convertJourneyFiltersForApi(journeyHistoryTabFilter),
                page: 1,
            };

            const response = await fetchApiAuth({
                method: 'GET',
                url: '/report/get-timesheet-report-csv', // or whatever your endpoint is
                params,
                responseType: 'blob', // Make sure to get raw data if it’s a CSV
            });

            const now = new Date();
            const dateStr = now.toISOString().slice(0, 10); // 'YYYY-MM-DD'
            const timeStr = now.toTimeString().split(' ')[0].replace(/:/g, '-'); // 'HH-MM-SS'
            const blob = new Blob([response.data], { type: 'application/csv' });
            saveAs(blob, `journey_history_${dateStr}_${timeStr}.csv`);
        } catch (err) {
            triggerErrorNotification(err.response ? err.response.data.message : err.message);
        } finally {
            this.setState({ isExportingJourneyHistoryCsv: false });
        }
    };

    fetchJourneyData = () => {
        const { match, driverViewEventFilters } = this.props;
        const { driverId } = match.params;

        let params = {
            driver: [driverId],
            asset_or_driver: 'drivers',
            journey_or_day: 'journeys',
            ...this.convertJourneyFiltersForApi(this.state.journeyHistoryTabFilter),
        };
        if (driverViewEventFilters?.length) {
            const matches = driverViewEventFilters.filter(f => parseInt(f.driverId) === parseInt(driverId));
            if (matches?.length) {
                params = matches[0].filters;
                params.asset_or_driver = 'drivers';
                params.journey_or_day = 'journeys';
            }
        }
        this.getDriverJourneys(params);
    };

    // ------------------ Driver Events (local) ------------------
    setEventsHistoryTabLoaded = () => {
        this.setState({ eventsHistoryTabLoaded: true });
    };

    onEventsHistoryTabFilterChange = (values) => {
        const { match, actions } = this.props;
        const { driverId } = match.params;
        const filters = { ...values };

        if (filters.date_from) {
            filters.date_from = `${filters.date_from.format('YYYY-MM-DD')} 00:00:00`;
        }
        if (filters.date_to) {
            filters.date_to = `${filters.date_to.format('YYYY-MM-DD')} 23:59:59`;
        }

        filters.page = 1;
        this.setState({ eventsHistoryTabFilter: filters }, () => {
            const params = {
                driverId,
                ...this.state.eventsHistoryTabFilter,
            };
            actions.setDriverViewEventFilters({
                driverId,
                filters: params,
            });
            this.getDriverEvents(params);
        });
    };

    onEventsHistoryTabPageChange = (page) => {
        const { match } = this.props;
        const { driverId } = match.params;

        this.setState(
            (prevState) => ({
                eventsHistoryTabFilter: {
                    ...prevState.eventsHistoryTabFilter,
                    page,
                },
            }),
            () => {
                this.getDriverEvents({
                    driverId,
                    ...this.state.eventsHistoryTabFilter,
                });
            },
        );
    };

    onEventsHistoryTabSortChange = (pagination, filters, sorter) => {
        const { match } = this.props;
        const { driverId } = match.params;

        const orderBy = sorter.field
            ? `${sorter.order === 'descend' ? '-' : ''}${sorter.field}`
            : null;

        this.getDriverEvents({
            driverId,
            ...this.state.eventsHistoryTabFilter,
            orderBy,
        });
    };

    async getDriverEvents(params) {
        if (params && !params.driverId) {
            return;
        }

        this.setState({ isLoadingEvents: true });

        try {
            const response = await fetchApiAuth({
                method: 'POST',
                url: `/driver/${params.driverId}/events-history`,
                params: {
                    page: params.page || 1,
                },
                body: params,
            });

            const {
                data,
                headers,
            } = response;

            // Example: read pagination from the headers
            const total = parseInt(headers['x-pagination-total-count'] || '0', 10);
            const perPage = parseInt(headers['x-pagination-per-page'] || '10', 10);
            const current = parseInt(headers['x-pagination-current-page'] || '1', 10);

            this.setState({
                driverEvents: data || [],
                driverEventsPagination: {
                    currentPage: current,
                    perPageCount: perPage,
                    totalRecords: total,
                },
            });
        } catch (e) {
            triggerErrorNotification(e.response ? e.response.data.message : e.message);
        } finally {
            // Turn off loading
            this.setState({ isLoadingEvents: false });
        }
    }

    onExportDriverEventCSV = async () => {
        const { match } = this.props;
        const { eventsHistoryTabFilter } = this.state;
        const { driverId } = match.params;

        this.setState({ isExportingDriverEventHistoryCsv: true });

        try {
            const response = await fetchApiAuth({
                method: 'POST',
                url: `/driver/${driverId}/events-history-csv-export`,
                body: {
                    ...eventsHistoryTabFilter,
                    driverId,
                    page: 1,
                },
            });

            const now = new Date();
            const formattedDate = now.toISOString().split('T')[0]; // YYYY-MM-DD
            const formattedTime = now.toTimeString().split(' ')[0].replace(/:/g, '-'); // HH-MM-SS

            // This assumes `response.data` is the CSV content
            const blob = new Blob([response.data], { type: 'application/csv' });
            saveAs(blob, `event_history_${formattedDate}_${formattedTime}.csv`);
        } catch (err) {
            triggerErrorNotification(err.response ? err.response.data.message : err.message);
        } finally {
            this.setState({ isExportingDriverEventHistoryCsv: false });
        }
    };

    // ------------------ Service Alerts (untouched) ------------------
    setServiceAlertsTabLoaded = () => {
        this.setState({ serviceAlertsTabLoaded: true });
    };

    onServiceAlertsTabPageChange = (page) => {
        const {
            match,
            actions,
        } = this.props;
        const { serviceAlertsTabFilter } = this.state;
        const { driverId } = match.params;

        this.setState({
            serviceAlertsTabFilter: {
                ...serviceAlertsTabFilter,
                page,
            },
        });
        const params = {
            driverId, ...serviceAlertsTabFilter,
            page,
        };
        actions.getDriverEventsRequest(params);
    };

    onServiceAlertsTabFilterChange = (values) => {
        const {
            match,
            actions,
        } = this.props;
        const { driverId } = match.params;

        const filters = {
            ...values,
            page: 1,
        };
        this.setState({ serviceAlertsTabFilter: filters });
        const params = { 
            driverId, 
            ...filters 
        };
        actions.setDriverViewManagementAlertFilters({
            driverId,
            filters: params,
        });
        actions.getDriverServiceAlertsRequest(params);
    };

    onServiceAlertsTabSortChange = (pagination, filters, sorter) => {
        const {
            match,
            actions,
        } = this.props;
        const { serviceAlertsTabFilter } = this.state;

        const { driverId } = match.params;
        const params = {
            driverId,
            orderBy: `${sorter.order === 'descend' ? '-' : ''}${sorter.field}`,
            ...serviceAlertsTabFilter,
        };
        actions.getDriverServiceAlertsRequest(params);
    };

    render() {
        return this.view();
    }
}

DriversView.propTypes = {
    actions: PropTypes.object.isRequired,
    driver: PropTypes.object.isRequired,
    asset: PropTypes.object.isRequired,
    driverId: PropTypes.number.isRequired,
    user: PropTypes.object.isRequired,
    driverJourneyFilters: PropTypes.object.isRequired,
};

function mapStateToProps(state, ownProps) {
    const driverId = parseInt(ownProps.match.params.driverId, 10);
    return {
        ...ownProps,
        driver: state.driver,
        asset: state.asset,
        driverId,
        tagStatus: state.driver.tagStatus,
        user: state.user,
        divisions: state.division.divisionOptions,
        driverList: state.driver.driverList,
        driverJourneyFilters: state.driver.driverJourneyFilters,
        isFetchingJourneys: state.report.isFetching,
        driverViewEventFilters: state.driver.driverViewEventFilters,
        driverViewJourneyFilters: state.driver.driverViewJourneyFilters,
    };
}

function mapDispatchToProps(dispatch) {
    return {
        actions: bindActionCreators(
            {
                ...DriverActions,
                ...AssetActions,
                ...DivisionActions,
                ...reportActions,
            },
            dispatch,
        ),
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(DriversView);
